import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryVideos } from '@/mocks/categories';

export default function CategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('tab') || 'trending');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && tab !== activeCategory) {
      setActiveCategory(tab);
    }
  }, [searchParams, activeCategory]);

  const handleCategoryChange = (catId: string) => {
    setActiveCategory(catId);
    setSearchParams({ tab: catId });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const activeData = categories.find((c) => c.id === activeCategory);
  const videos = categoryVideos[activeCategory] || [];

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: '分类浏览' },
  ];

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `抖阴pro - ${activeData?.name || '分类浏览'}`,
    description: activeData?.description || '抖阴pro短视频内容分类浏览',
    url: `https://douyinpro.com/category?tab=${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '抖阴pro',
      url: 'https://douyinpro.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                分类浏览
              </h1>
              <p className="text-foreground-600 max-w-2xl leading-relaxed">
                按内容分类浏览您感兴趣的短视频，发现更多精彩
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {activeData && (
              <div className="mb-8 p-6 rounded-2xl bg-background-100 border border-background-200/70">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className={`${activeData.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-foreground-950">
                    {activeData.name}
                  </h2>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed ml-13">
                  {activeData.description}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200/70 transition-all duration-300 hover:translate-y-[-2px] cursor-pointer"
                >
                  <div className="relative aspect-[9/16] overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/70 text-background-50 text-xs">
                      {video.duration}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <i className="ri-play-fill text-background-50 text-sm"></i>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug mb-1">
                      {video.title}
                    </h3>
                    <p className="text-xs text-foreground-500">{video.author}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {video.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-heart-line"></i>
                        {video.likes}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}